/*
Copyright (c) 2012-2023 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import {
  AdvancedPricingConfig,
  CharacteristicConfigEntry,
  FEConfig,
  FEConfigData,
} from "store/duck/global.type"
import { AdvancedPricing, OutletStore, OutletStoreV2 } from "types/models/menu"
import { currencyToMinorUnit, minorUnitToCurrency } from "utils/currency"
import { cloneDeep } from "lodash"
import {
  isFeatureFlagEnabled,
  FeatureFlagBitPosition,
  FeatureFlagBitV2Position,
  isFeatureFlagV2Enabled,
} from "utils/menu_store_helper"

// https://gitlab.myteksi.net/gophers/go/-/blob/master/food/common/pb/catalogpb/characteristic.proto
export const characteristicEnums = {
  serviceType: {
    delivery: "Delivery",
    selfPickUp: "SelfPickUp",
    dineIn: "DineIn",
  },
  orderType: {
    onDemand: "OnDemand",
    scheduled: "Scheduled",
  },
  channelType: {
    defaultChannel: "DefaultChannel", // unused anywhere - this is a legacy enum. kept for consistency's sake with above proto
    grabApp: "GrabApp",
    storeFront: "StoreFront",
    grabWeb: "GrabWeb",
  },
} as const

type OutletFieldsToDisallowedKeysEntry = {
  key: string
  isKeyDisallowedForOutlet?: (
    val: any,
    outlet: OutletStore | OutletStoreV2,
  ) => boolean
}

type OutletFieldsToDisallowedKeys = Partial<
  Record<keyof OutletStore, OutletFieldsToDisallowedKeysEntry>
>

type OutletFieldsToDisallowedKeysV2 = Partial<
  Record<keyof OutletStoreV2, OutletFieldsToDisallowedKeysEntry>
>

const defaultIsKeyDisallowedForOutlet = (val: any) => !val

const fieldsToDisallowedKeys: OutletFieldsToDisallowedKeys = {
  supportDelivery: {
    key: characteristicEnums.serviceType.delivery,
  },
  supportDineIn: {
    key: characteristicEnums.serviceType.dineIn,
  },
  supportTakeaway: {
    key: characteristicEnums.serviceType.selfPickUp,
  },
  blockOnDemandOrders: {
    key: characteristicEnums.orderType.onDemand,
    isKeyDisallowedForOutlet: (val, outlet) =>
      !!outlet.isScheduledOrderEnabled && !!val,
  },
  isScheduledOrderEnabled: {
    key: characteristicEnums.orderType.scheduled,
  },
  storefrontEnabled: {
    key: characteristicEnums.channelType.storeFront,
  },
}

const fieldsToDisallowedKeysV2: OutletFieldsToDisallowedKeysV2 = {
  supportDelivery: {
    key: characteristicEnums.serviceType.delivery,
  },
  supportDineIn: {
    key: characteristicEnums.serviceType.dineIn,
  },
  supportTakeaway: {
    key: characteristicEnums.serviceType.selfPickUp,
  },
  blockOnDemandOrders: {
    key: characteristicEnums.orderType.onDemand,
    isKeyDisallowedForOutlet: (val, outlet) =>
      !!outlet.isScheduledOrderEnabled && !!val,
  },
  supportScheduledOrders: {
    key: characteristicEnums.orderType.scheduled,
  },
  storefrontEnabled: {
    key: characteristicEnums.channelType.storeFront,
  },
}

export function findDisallowedSOCKeys(outlet: OutletStore): Set<string> {
  const disallowedSOCKeys = new Set<string>() // assumption: all SOC keys are unique
  Object.entries(fieldsToDisallowedKeys).forEach(
    ([
      outletKey,
      { key, isKeyDisallowedForOutlet = defaultIsKeyDisallowedForOutlet },
    ]: [string, OutletFieldsToDisallowedKeysEntry]) => {
      if (
        isKeyDisallowedForOutlet(outlet[outletKey as keyof OutletStore], outlet)
      ) {
        disallowedSOCKeys.add(key)
      }
    },
  )
  return disallowedSOCKeys
}

export function findDisallowedSOCKeysV2(outletV2: OutletStoreV2): Set<string> {
  const disallowedSOCKeys = new Set<string>() // assumption: all SOC keys are unique
  Object.entries(fieldsToDisallowedKeysV2).forEach(
    ([
      outletKey,
      { key, isKeyDisallowedForOutlet = defaultIsKeyDisallowedForOutlet },
    ]: [string, OutletFieldsToDisallowedKeysEntry]) => {
      if (
        isKeyDisallowedForOutlet(
          outletV2[outletKey as keyof OutletStoreV2],
          outletV2,
        )
      ) {
        disallowedSOCKeys.add(key)
      }
    },
  )
  return disallowedSOCKeys
}

function removeKeysThatAreNotAllowed(
  disallowedSOCKeys: Set<string>,
  key: string,
): string {
  return key
    .split("&")
    .filter(
      splitKey => !splitKey.split("_").some(e => disallowedSOCKeys.has(e)),
    )
    .join("&")
}

export const getLocalisedName = (
  { key, name }: CharacteristicConfigEntry,
  locale: string,
  disallowedSOCKeys: Set<string>,
): string => {
  const translation =
    name[removeKeysThatAreNotAllowed(disallowedSOCKeys, key)]?.translation ?? {}
  return translation[locale] ?? translation.en ?? "-"
}

/* istanbul ignore next this is so that our keyboard doesn't break typing the same thing over and over again */
export function getIsServiceBasedMenuEnabled(
  outlet: OutletStore | null | undefined,
) {
  return isFeatureFlagEnabled(FeatureFlagBitPosition.serviceBasedMenu, outlet)
}

export function getIsServiceBasedMenuV2Enabled(
  outlet: OutletStoreV2 | null | undefined,
) {
  return isFeatureFlagV2Enabled(
    FeatureFlagBitV2Position.serviceBasedMenu,
    outlet,
  )
}

export function filterFEConfigDataCharacteristicStrategy(
  argFeConfigData: FEConfigData | undefined,
  outlet?: OutletStore | null | undefined,
  outletV2?: OutletStoreV2 | null | undefined,
  enableTroyNewMerchantEndpoint?: boolean,
) {
  const feConfigData = cloneDeep(argFeConfigData)
  let disallowedSOCKeysSet = new Set<string>()
  if (enableTroyNewMerchantEndpoint && outletV2) {
    disallowedSOCKeysSet = findDisallowedSOCKeysV2(outletV2)
  } else if (outlet) {
    disallowedSOCKeysSet = findDisallowedSOCKeys(outlet)
  }
  if (feConfigData?.serviceBasedMenuConfig?.characteristicStrategy) {
    const {
      purchasableConfig = [],
      pricingConfig = [],
    } = feConfigData.serviceBasedMenuConfig.characteristicStrategy
    const isAllowedSOCKeys = (cfg: { key: string }) =>
      cfg.key &&
      !cfg.key.split("&").every(cfgKey =>
        cfgKey.split("_").some(
          socKey => disallowedSOCKeysSet.has(socKey), // only disable SOCs that are ALL disallowed, otherwise keep the compound key
        ),
      )
    const filteredPricingConfig = pricingConfig
      .map(cfg => ({
        ...cfg,
        ...(cfg.channels
          ? {
              channels: cfg.channels.filter(isAllowedSOCKeys),
            }
          : {}),
      }))
      .filter(
        cfg =>
          isAllowedSOCKeys(cfg) && (!cfg.channels || cfg.channels.length > 0),
      )
    const filteredPurchasableConfig = purchasableConfig.filter(isAllowedSOCKeys)
    feConfigData.serviceBasedMenuConfig.characteristicStrategy = {
      purchasableConfig: filteredPurchasableConfig,
      pricingConfig: filteredPricingConfig,
    }
  }
  return {
    filteredFEConfigData: feConfigData,
    disallowedSOCKeys: disallowedSOCKeysSet,
  }
}

export interface AdditionalPrice {
  regular: string | null
  [k: string]: string | null
}

export function additionalPriceToPriceRange(
  additionalPrice?: AdditionalPrice,
): { min: string; max: string } | undefined {
  if (!additionalPrice) {
    return undefined
  }
  let min = Infinity
  let max = -1
  Object.values(additionalPrice).forEach(val => {
    if (val === null) {
      // because Number(null) = 0, sigh...
      return
    }
    const valNum = Number(val)
    if (Number.isNaN(valNum)) {
      return
    }
    if (valNum < min) {
      min = valNum
    }
    if (valNum > max) {
      max = valNum
    }
  })
  if (min === Infinity || max === -1) {
    // we haven't found any values here, so technically there's no min-max
    return undefined
  }
  return {
    min: String(min),
    max: String(max),
  }
}

export function additionalPriceToItemAdvancedPricing(
  additionalPrice: AdditionalPrice,
  currency?: string,
): AdvancedPricing {
  return Object.entries(additionalPrice)
    .filter(([k, v]) => k !== "regular" && v !== null)
    .reduce<AdvancedPricing>((acc, curr) => {
      const [advancedPricingKey, advancedPricingVal] = curr
      const advancedPricingPriceInMin = currencyToMinorUnit(
        advancedPricingVal!,
        currency,
      )
      // split the keys based on the & delimiter
      const splitKeys = advancedPricingKey.split("&")
      const expandedAdvancedPricing = splitKeys.reduce<AdvancedPricing>(
        (expandedAdvancedPricingAcc, splitKey) => ({
          ...expandedAdvancedPricingAcc,
          [splitKey]: {
            priceInMin: advancedPricingPriceInMin,
            priceDisplay: "",
          },
        }),
        {},
      )
      return {
        ...acc,
        ...expandedAdvancedPricing,
      }
    }, {})
}

export function advancedPricingToAdditionalPrice(
  advancedPricing?: AdvancedPricing,
  currency?: string,
): Omit<AdditionalPrice, "regular"> {
  return Object.entries(advancedPricing ?? {}).reduce<
    Omit<AdditionalPrice, "regular">
  >(
    (acc, [key, val]) => ({
      ...acc,
      [key]: String(minorUnitToCurrency(val.priceInMin, currency)),
    }),
    {},
  )
}

/* istanbul ignore next because this is just a mapping function */
export function getAdvancedPricingValidationConfig(
  validationType: "modifier" | "item",
  feConfig: FEConfig,
): AdvancedPricingConfig | undefined {
  switch (validationType) {
    case "item":
      return feConfig.data?.serviceBasedMenuConfig?.itemAdvancedPricingConfig
    case "modifier":
      return feConfig.data?.serviceBasedMenuConfig
        ?.modifierAdvancedPricingConfig
    default:
      return undefined
  }
}
